import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Shield, Search, UserCog, Save, ChevronDown, ChevronUp, Key } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "clan", label: "Član" },
  { value: "pripravnik", label: "Pripravnik" },
  { value: "predsednik", label: "Predsednik" },
  { value: "tajnik", label: "Tajnik" },
  { value: "gospodar", label: "Gospodar" },
  { value: "blagajnik", label: "Blagajnik" },
  { value: "upravnik_lovske_koce", label: "Upravnik lovske koče" },
  { value: "skupinovodja", label: "Skupinovodja" },
  { value: "kinoloski_referent", label: "Kinološki referent" },
  { value: "referent_strelstvo", label: "Referent za strelstvo" },
  { value: "komisija_skode", label: "Komisija za škode" },
  { value: "komisija_izobrazevanje", label: "Komisija za izobraževanje" },
  { value: "komisija_trofeje", label: "Komisija za trofeje" },
  { value: "disciplinska_komisija", label: "Disciplinska komisija" },
  { value: "administrator", label: "Administrator (Tehnični skrbnik)" },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(r => [r.value, r.label]));

const RolesPage = () => {
  const { token, user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingRoleByUser, setPendingRoleByUser] = useState({}); // { userId: newRole }
  const [pendingPermsByUser, setPendingPermsByUser] = useState({}); // { userId: [perms] }
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [permCatalog, setPermCatalog] = useState([]);
  const [expandedPerms, setExpandedPerms] = useState({});

  const fetchMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/users`, { headers });
      const sorted = (res.data || []).sort((a, b) =>
        `${a.priimek} ${a.ime}`.localeCompare(`${b.priimek} ${b.ime}`, "sl")
      );
      setMembers(sorted);
    } catch (e) {
      toast.error("Napaka pri pridobivanju članov");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // Load permission catalog
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/permissions/catalog`, { headers });
        setPermCatalog(res.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = (uid, role) => {
    setPendingRoleByUser((prev) => ({ ...prev, [uid]: role }));
  };

  const togglePerm = (member, permKey) => {
    setPendingPermsByUser((prev) => {
      const current = prev[member.id] || member.permissions || [];
      const next = current.includes(permKey)
        ? current.filter((p) => p !== permKey)
        : [...current, permKey];
      return { ...prev, [member.id]: next };
    });
  };

  const saveRole = async (member) => {
    const newRole = pendingRoleByUser[member.id];
    const newPerms = pendingPermsByUser[member.id];
    const roleChanged = newRole && newRole !== member.vloga;
    const permsChanged =
      newPerms &&
      JSON.stringify([...(newPerms || [])].sort()) !==
        JSON.stringify([...(member.permissions || [])].sort());
    if (!roleChanged && !permsChanged) return;

    setSavingId(member.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {};
      if (roleChanged) payload.vloga = newRole;
      if (permsChanged) payload.permissions = newPerms;
      await axios.put(`${API}/users/${member.id}`, payload, { headers });
      toast.success(`Posodobljeno: ${member.ime} ${member.priimek}`);
      setPendingRoleByUser((prev) => { const next = { ...prev }; delete next[member.id]; return next; });
      setPendingPermsByUser((prev) => { const next = { ...prev }; delete next[member.id]; return next; });
      await fetchMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (filterRole && m.vloga !== filterRole) return false;
      if (!q) return true;
      const hay = `${m.ime || ""} ${m.priimek || ""} ${m.lovska_izkaznica || ""} ${m.stevilka_clana || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [members, search, filterRole]);

  // Counts by role
  const counts = useMemo(() => {
    const c = {};
    members.forEach((m) => { c[m.vloga] = (c[m.vloga] || 0) + 1; });
    return c;
  }, [members]);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="roles-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <Shield className="h-7 w-7" />
          Dodeljevanje vlog
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Pregled članov in dodelitev vlog. Sprememba vloge spremeni dovoljenja v aplikaciji.
        </p>
      </div>

      {/* Role counts summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pregled vlog</CardTitle>
          <CardDescription>Število članov v vsaki vlogi</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {ROLE_OPTIONS.filter(r => counts[r.value]).map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setFilterRole(filterRole === r.value ? "" : r.value)}
                className={`rounded-md border px-3 py-1.5 text-xs transition ${
                  filterRole === r.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/40 hover:bg-muted text-foreground"
                }`}
                data-testid={`role-filter-${r.value}`}
              >
                {r.label}: <strong>{counts[r.value]}</strong>
              </button>
            ))}
            {filterRole && (
              <Button variant="ghost" size="sm" onClick={() => setFilterRole("")}>Počisti filter</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Members list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCog className="h-4 w-4" />
            Člani ({filtered.length} / {members.length})
          </CardTitle>
          <div className="relative mt-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Iskanje po imenu, priimku, lovski izkaznici ali št. člana..."
              className="pl-8 h-9"
              data-testid="roles-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center text-muted-foreground text-sm py-12">Nalaganje članov...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-12">Ni rezultatov.</div>
          ) : (
            <ScrollArea className="h-[calc(100vh-360px)] min-h-[400px]">
              <div className="divide-y">
                {filtered.map((m) => {
                  const isSelf = m.id === currentUser?.id;
                  const pending = pendingRoleByUser[m.id];
                  const pendingPerms = pendingPermsByUser[m.id];
                  const currentPerms = pendingPerms ?? (m.permissions || []);
                  const dirty =
                    (!!pending && pending !== m.vloga) ||
                    (pendingPerms && JSON.stringify([...(pendingPerms || [])].sort()) !== JSON.stringify([...(m.permissions || [])].sort()));
                  const fullName = `${m.priimek || ""} ${m.ime || ""}`.trim();
                  const isExpanded = !!expandedPerms[m.id];
                  return (
                    <div
                      key={m.id}
                      className="px-4 py-2.5 hover:bg-muted/30 transition"
                      data-testid={`roles-row-${m.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">{fullName}</span>
                            {isSelf && (
                              <Badge variant="outline" className="text-[10px]">Vi</Badge>
                            )}
                            <Badge variant="secondary" className="text-[10px]">
                              {ROLE_LABEL[m.vloga] || m.vloga}
                            </Badge>
                            {currentPerms.length > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">
                                +{currentPerms.length} dovoljenj
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            LIZ: {m.lovska_izkaznica || "—"} • Št. člana: {m.stevilka_clana || "—"}
                            {m.email && <> • {m.email}</>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Select
                            value={pending || m.vloga}
                            onValueChange={(v) => handleRoleChange(m.id, v)}
                            disabled={savingId === m.id}
                          >
                            <SelectTrigger
                              className="h-8 w-56 text-xs"
                              data-testid={`role-select-${m.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map((r) => (
                                <SelectItem key={r.value} value={r.value} className="text-xs">
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {permCatalog.length > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedPerms((s) => ({ ...s, [m.id]: !s[m.id] }))}
                              className="h-8"
                              data-testid={`role-perms-toggle-${m.id}`}
                            >
                              <Key className="h-3.5 w-3.5 mr-1" />
                              Dovoljenja
                              {isExpanded ? (
                                <ChevronUp className="h-3.5 w-3.5 ml-1" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5 ml-1" />
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={dirty ? "default" : "outline"}
                            disabled={!dirty || savingId === m.id}
                            onClick={() => saveRole(m)}
                            className="h-8"
                            data-testid={`role-save-${m.id}`}
                          >
                            <Save className="h-3.5 w-3.5 mr-1" />
                            Shrani
                          </Button>
                        </div>
                      </div>

                      {isExpanded && permCatalog.length > 0 && (
                        <div className="mt-2 ml-3 sm:ml-6 rounded-md border bg-muted/30 p-3" data-testid={`perms-panel-${m.id}`}>
                          <div className="text-[11px] font-semibold mb-2 text-foreground">
                            Specifična dovoljenja za tega člana (poleg vloge):
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {permCatalog.map((p) => {
                              const checked = currentPerms.includes(p.key);
                              return (
                                <label key={p.key} className="flex items-start gap-2 text-xs cursor-pointer">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => togglePerm(m, p.key)}
                                    className="mt-0.5"
                                    data-testid={`perm-${m.id}-${p.key}`}
                                  />
                                  <span className="break-words leading-tight">{p.label}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="mt-2 text-[10px] text-muted-foreground">
                            Dovoljenja so dodatek k vlogi — predsednik samodejno dobi vse pravice.
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground border-t pt-2">
        ⚠️ Pred spremembo svoje vloge dobro premislite — če si odvzamete predsednikove pravice, ne boste več mogli dostopati do te strani.
      </div>
    </div>
  );
};

export default RolesPage;
